import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, ArrowRight, ArrowLeft, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { pixel } from "@/lib/metaPixel";
import WhatsAppCta, { useWhatsAppCta } from "@/components/WhatsAppCta";

const RESEND_COOLDOWN = 30; // seconds — mirror backend

export default function Signin() {
  const cta = useWhatsAppCta();
  const waLive = cta.channel === "whatsapp";
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const nav = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const tickRef = useRef(null);

  // Seamless login: skip OTP for returning users with a valid token.
  useEffect(() => {
    if (!authLoading && user) {
      nav(user.onboarded ? "/app/dashboard" : "/onboard", { replace: true });
    }
  }, [authLoading, user, nav]);

  // Resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    tickRef.current = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(tickRef.current);
  }, [cooldown]);

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const requestOtp = async (e) => {
    e?.preventDefault();
    if (!emailIsValid) {
      toast.error("Enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/request-otp", { email: email.trim().toLowerCase() });
      setGeneratedOtp(r.data.otp || "");
      setStep("otp");
      setCooldown(RESEND_COOLDOWN);
      toast.success(r.data.otp ? "OTP generated (dev mode)" : `Code sent to ${email}`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        const m = /(\d+)s/.exec(err.response?.data?.detail || "");
        if (m) setCooldown(parseInt(m[1], 10));
      }
      toast.error(err.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/verify-otp", { email: email.trim().toLowerCase(), otp });
      login(r.data.token, r.data.user);
      // Meta Pixel: fire CompleteRegistration exactly once for new signups.
      if (r.data.is_new) {
        pixel.completeRegistration({ status: true, content_name: "Kobolog signup" });
      }
      toast.success(r.data.is_new ? "Welcome to Kobolog AI!" : "Welcome back!");
      if (r.data.user.onboarded) nav("/app/dashboard");
      else nav("/onboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-kudi-cream flex flex-col">
      <header className="px-5 py-4">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-kudi-ink/70 hover:text-kudi-green" data-testid="back-to-landing">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-kudi-green flex items-center justify-center text-white font-heading font-bold text-2xl mb-4">K</div>
            <h1 className="font-heading text-3xl font-bold">
              {step === "email" ? "Welcome to Kobolog AI" : "Enter your code"}
            </h1>
            <p className="mt-2 text-kudi-ink/65">
              {step === "email" ? "Sign in with your email — we'll send you a 6-digit code" : `Sent to ${email}`}
            </p>
          </div>

          {step === "email" && waLive && (
            <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4" data-testid="wa-nudge">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884z"/></svg>
                </div>
                <div className="flex-1">
                  <div className="font-heading text-sm font-bold text-emerald-900">Prefer WhatsApp?</div>
                  <p className="text-xs text-emerald-900/80 mt-0.5 mb-2.5">No password, no app to learn. Just chat with us like a friend.</p>
                  <WhatsAppCta testid="signin-wa-cta" label="Open in WhatsApp" size="sm" showArrow={false} />
                </div>
              </div>
            </div>
          )}

          {step === "email" ? (
            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-kudi-ink/80">Email address</label>
                <div className="mt-1.5 relative">
                  <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-kudi-ink/40" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    inputMode="email"
                    className="w-full bg-white border border-border rounded-xl pl-11 pr-4 py-4 text-base focus:outline-none focus:ring-2 focus:ring-kudi-green/20 focus:border-kudi-green transition"
                    data-testid="email-input"
                    autoFocus
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !emailIsValid}
                className="w-full bg-kudi-green text-white rounded-full py-4 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-50 active:scale-[0.98]"
                data-testid="send-otp-btn"
              >
                {loading ? "Sending..." : <>Send code <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-center text-xs text-kudi-ink/50 mt-6">
                By continuing you agree to our Terms & Privacy
              </p>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4">
              {generatedOtp && (
                <div className="bg-accent border border-kudi-green/20 rounded-xl p-3 text-center text-sm" data-testid="otp-display">
                  <span className="text-kudi-ink/70">Dev mode OTP: </span>
                  <span className="font-mono font-bold text-kudi-green text-lg tracking-widest">{generatedOtp}</span>
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-kudi-ink/80">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="• • • • • •"
                  className="mt-1.5 w-full bg-white border border-border rounded-xl px-4 py-4 text-2xl tracking-[0.6em] text-center font-mono font-semibold focus:outline-none focus:ring-2 focus:ring-kudi-green/20 focus:border-kudi-green transition"
                  data-testid="otp-input"
                  autoFocus
                />
                <p className="text-xs text-kudi-ink/50 mt-2 text-center">
                  Code expires in 5 minutes. Up to 5 attempts.
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-kudi-green text-white rounded-full py-4 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-green-dark transition disabled:opacity-50 active:scale-[0.98]"
                data-testid="verify-otp-btn"
              >
                {loading ? "Verifying..." : <>Verify & continue <ArrowRight className="w-4 h-4" /></>}
              </button>
              <div className="flex items-center justify-between pt-2 text-sm">
                <button
                  type="button"
                  onClick={() => { setStep("email"); setOtp(""); setGeneratedOtp(""); }}
                  className="text-kudi-ink/60 hover:text-kudi-green"
                  data-testid="change-email-btn"
                >
                  Change email
                </button>
                <button
                  type="button"
                  onClick={() => cooldown <= 0 && requestOtp()}
                  disabled={cooldown > 0 || loading}
                  className="inline-flex items-center gap-1.5 text-kudi-green disabled:text-kudi-ink/40 disabled:cursor-not-allowed font-medium"
                  data-testid="resend-otp-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
